import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Save, MapPin, Phone, Clock, Link as LinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    club_name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    hours: "",
    closed_note: "",
    facebook_url: "",
    instagram_url: "",
    tiktok_url: "",
    map_embed_url: "",
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API}/contact`);
        setFormData({
          club_name: response.data.club_name || "",
          address: response.data.address || "",
          city: response.data.city || "",
          state: response.data.state || "",
          zip_code: response.data.zip_code || "",
          phone: response.data.phone || "",
          hours: response.data.hours || "",
          closed_note: response.data.closed_note || "",
          facebook_url: response.data.facebook_url || "",
          instagram_url: response.data.instagram_url || "",
          tiktok_url: response.data.tiktok_url || "",
          map_embed_url: response.data.map_embed_url || "",
        });
      } catch (err) {
        logger.error("Error fetching contact:", err);
        toast.error("Failed to load contact info");
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/contact`, formData, getAuthHeaders());
      toast.success("Contact info updated!");
    } catch (err) {
      toast.error("Failed to update contact info");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading contact info...</div>;
  }

  return (
    <div data-testid="contact-manager">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-white mb-2">Contact Manager</h1>
        <p className="text-white/60">Update your contact information and social links</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
        {/* Basic Info */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-xl text-white">Location</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Club Name</Label>
              <Input
                value={formData.club_name}
                onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="San Marcos Social Club"
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="700 N LBJ, Ste 103A"
                data-testid="contact-address-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-white/80">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="San Marcos"
                  data-testid="contact-city-input"
                />
              </div>
              <div>
                <Label className="text-white/80">State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="TX"
                  data-testid="contact-state-input"
                />
              </div>
              <div>
                <Label className="text-white/80">ZIP Code</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="78666"
                  data-testid="contact-zip-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Phone & Hours */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-xl text-white">Hours & Phone</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="737-355-2317"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Hours</Label>
                <Input
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="3 PM – 4 AM"
                  data-testid="contact-hours-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Closed Note</Label>
              <Input
                value={formData.closed_note}
                onChange={(e) => setFormData({ ...formData, closed_note: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="Closed Major Holidays"
                data-testid="contact-closed-input"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <LinkIcon className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-xl text-white">Social Links</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Facebook URL</Label>
              <Input
                value={formData.facebook_url}
                onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://facebook.com/..."
                data-testid="contact-facebook-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Instagram URL</Label>
              <Input
                value={formData.instagram_url}
                onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://instagram.com/..."
                data-testid="contact-instagram-input"
              />
            </div>

            <div>
              <Label className="text-white/80">TikTok URL</Label>
              <Input
                value={formData.tiktok_url}
                onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://tiktok.com/..."
                data-testid="contact-tiktok-input"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-gold text-black hover:bg-gold-dark px-8"
            data-testid="save-contact-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
